import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const UNITA = [
  { v: "giorni", one: "Giorno", many: "Giorni" },
  { v: "settimane", one: "Settimana", many: "Settimane" },
  { v: "mesi", one: "Mese", many: "Mesi" },
  { v: "anni", one: "Anno", many: "Anni" },
];

const GIORNI = [
  { v: "0", l: "Lunedì" }, { v: "1", l: "Martedì" }, { v: "2", l: "Mercoledì" },
  { v: "3", l: "Giovedì" }, { v: "4", l: "Venerdì" }, { v: "5", l: "Sabato" }, { v: "6", l: "Domenica" },
];
const GIORNI_MESE = Array.from({ length: 31 }, (_, i) => String(i + 1));

export function ricorrenzaCustomLabel(custom) {
  if (!custom) return "";
  const n = Number(custom.intervallo) || 1;
  const u = UNITA.find((x) => x.v === custom.unita) || UNITA[0];
  const unitTxt = (n === 1 ? u.one : u.many).toLowerCase();
  const base = n === 1 ? `ogni ${unitTxt}` : `ogni ${n} ${unitTxt}`;
  if (custom.unita === "settimane") {
    const giorni = Array.isArray(custom.notifica_giorni_settimana) ? custom.notifica_giorni_settimana
      : (custom.notifica_giorno_settimana !== null && custom.notifica_giorno_settimana !== undefined && custom.notifica_giorno_settimana !== "" ? [custom.notifica_giorno_settimana] : []);
    const labels = giorni.map((g) => GIORNI.find((x) => x.v === String(g))?.l).filter(Boolean);
    if (labels.length) return `${base} · avviso ${labels.join(", ")}`;
  }
  if (custom.unita === "mesi") {
    const d = custom.notifica_giorno_mese;
    if (d !== null && d !== undefined && d !== "") return `${base} · avviso il giorno ${d}`;
  }
  return base;
}

export function RicorrenzaCustomDialog({ open, onOpenChange, value, onSave }) {
  const [n, setN] = useState(1);
  const [unita, setUnita] = useState("giorni");
  const [giorniSett, setGiorniSett] = useState([]);
  const [giornoMese, setGiornoMese] = useState("none");
  useEffect(() => {
    if (open) {
      setN(Number(value?.intervallo) || 1);
      setUnita(value?.unita || "giorni");
      const arr = Array.isArray(value?.notifica_giorni_settimana) ? value.notifica_giorni_settimana.map(String)
        : (value?.notifica_giorno_settimana !== null && value?.notifica_giorno_settimana !== undefined && value?.notifica_giorno_settimana !== "" ? [String(value.notifica_giorno_settimana)] : []);
      setGiorniSett(arr);
      const gm = value?.notifica_giorno_mese;
      setGiornoMese(gm === null || gm === undefined || gm === "" ? "none" : String(gm));
    }
  }, [open, value]);
  const summaryExtra =
    unita === "settimane" ? (giorniSett.length ? ` Notifica il ${giorniSett.map((v) => GIORNI.find((g) => g.v === v)?.l).filter(Boolean).join(", ")}.` : "")
    : unita === "mesi" ? (giornoMese !== "none" ? ` Notifica il giorno ${giornoMese} del mese.` : "")
    : "";
  const summary = `Si tiene ${ricorrenzaCustomLabel({ intervallo: n, unita })}.` + summaryExtra;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]" data-testid="ricorrenza-custom-dialog">
        <DialogHeader><DialogTitle className="font-heading">Ricorrenza personalizzata</DialogTitle></DialogHeader>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Si ripete ogni</Label>
            <Input type="number" min={1} value={n}
              onChange={(e) => setN(Math.max(1, parseInt(e.target.value, 10) || 1))}
              className="mt-1 w-24 rounded-sm" data-testid="ricorrenza-interval" />
          </div>
          <Select value={unita} onValueChange={setUnita}>
            <SelectTrigger className="rounded-sm w-40" data-testid="ricorrenza-unita"><SelectValue /></SelectTrigger>
            <SelectContent>{UNITA.map((x) => <SelectItem key={x.v} value={x.v}>{Number(n) === 1 ? x.one : x.many}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        {unita === "settimane" && (
          <div className="rounded-sm border border-border bg-secondary/40 p-3 mt-1">
            <Label className="text-xs uppercase tracking-wider">Giorni della settimana per la notifica</Label>
            <p className="text-[11px] text-muted-foreground mb-2">Seleziona uno o più giorni (dal lunedì alla domenica) in cui ricevere l'avviso di questa scadenza.</p>
            <div className="flex flex-wrap gap-1.5" data-testid="ricorrenza-giorni-settimana">
              {GIORNI.map((x) => {
                const active = giorniSett.includes(x.v);
                return (
                  <button type="button" key={x.v} data-testid={`ricorrenza-giorno-${x.v}`}
                    onClick={() => setGiorniSett((prev) => prev.includes(x.v) ? prev.filter((d) => d !== x.v) : [...prev, x.v])}
                    className={`px-3 py-1.5 rounded-sm text-sm border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-white border-input hover:border-foreground"}`}>
                    {x.l}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {unita === "mesi" && (
          <div className="rounded-sm border border-border bg-secondary/40 p-3 mt-1">
            <Label className="text-xs uppercase tracking-wider">Giorno del mese per la notifica</Label>
            <p className="text-[11px] text-muted-foreground mb-2">In quale giorno del mese (dal 1 al 31) vuoi ricevere l'avviso di questa scadenza.</p>
            <Select value={giornoMese} onValueChange={setGiornoMese}>
              <SelectTrigger className="rounded-sm w-full" data-testid="ricorrenza-giorno-mese"><SelectValue placeholder="Nessuna preferenza" /></SelectTrigger>
              <SelectContent className="max-h-64">
                <SelectItem value="none">Nessuna preferenza</SelectItem>
                {GIORNI_MESE.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}

        <p className="text-sm text-muted-foreground" data-testid="ricorrenza-summary">{summary}</p>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button className="rounded-sm" data-testid="ricorrenza-save"
            onClick={() => {
              const giorniNum = giorniSett.map(Number).sort((a, b) => a - b);
              onSave({
                intervallo: Number(n) || 1,
                unita,
                notifica_giorni_settimana: unita === "settimane" ? giorniNum : [],
                notifica_giorno_settimana: unita === "settimane" && giorniNum.length ? giorniNum[0] : null,
                notifica_giorno_mese: unita === "mesi" && giornoMese !== "none" ? Number(giornoMese) : null,
              });
              onOpenChange(false);
            }}>Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
